/* ---------------------------------------------------------------------------
   The status date and the forecast (LC-44).

   The variance in ./variance.ts says how late each activity is against its own
   planned dates. It cannot say when the bridge will be finished, because a
   pile cap three weeks late with forty days of float does not move the
   handover, and a bent cap nine days late on the critical path moves it nine
   days. That needs the logic, which is why the baseline now keeps its links.

   ══ WHAT THE RE-SOLVE TAKES ══

     · the frozen baseline's activities, durations (working days) and links;
     · its calendar;
     · what has happened, as at the status date, through the matches and the
       ACCEPTED entries — the same `VarianceRow`s the variance is computed
       from, so the two can never disagree about what has started;
     · the status date. Remaining work is scheduled on or after it.

   ══ THE RULES, WHICH ARE P6'S DEFAULTS WITH ONE STATED ══

     · A COMPLETE activity keeps its actual dates: first accepted entry of its
       kind to the declared finish. With no entry, the declared finish is both.
     · An activity IN PROGRESS keeps its actual start. Its remaining duration
       is its planned duration less the working days already elapsed since that
       start, never less than one — it is not finished until somebody says so —
       and it runs from the status date. PROGRESS OVERRIDES LOGIC: an activity
       that started out of sequence is not pulled back behind its predecessor.
     · An activity NOT STARTED — or unmatched, about which nothing is known —
       starts no earlier than the status date, its predecessors' logic, and any
       start the planner pinned.

   Then the ordinary forward and backward passes (./cpm.ts). The latest
   forecast finish is the forecast completion; activities not complete with no
   float are the critical path as it stands today.

   A BASELINE WITH NO LINKS — frozen before LC-44 kept them — has no logic to
   re-solve. Each activity is then held to its planned start as well, so the
   forecast is the plan pushed by the status date and by nothing else, and it
   says so (`logic: 'dates'`).

   Pure, and taking the status date rather than reading the clock.
   --------------------------------------------------------------------------- */

import { CalendarIndex, EVERY_DAY, workingDaysBetween, type WorkCalendar } from './calendar.ts';
import { solveCpm, type CpmLink, type CpmNode } from './cpm.ts';
import { addDays } from './periods.ts';
import { signedDays, type Baseline, type BaselineActivity, type VarianceRow } from './variance.ts';

export interface ForecastRow {
  activityId: string;
  name: string;
  kind: string;
  plannedStart: string;
  plannedFinish: string;
  forecastStart: string;
  forecastFinish: string;
  /** Working days. Null for a complete activity, which has none left to use. */
  floatDays: number | null;
  critical: boolean;
  state: 'complete' | 'in-progress' | 'not-started';
  /** Forecast finish against planned finish, calendar days. Positive is late. */
  slipDays: number;
}

export interface Forecast {
  statusDate: string;
  rows: ForecastRow[];
  /** The latest planned finish of any activity. */
  baselineFinish: string;
  /** The latest forecast finish. */
  forecastFinish: string;
  /** Calendar days between the two. Positive is late. */
  slipDays: number;
  /** And in working days on the contract's calendar. */
  slipWorkingDays: number;
  /** Not complete, and no float: in forecast start order. */
  criticalPath: ForecastRow[];
  /** `links` where the stored logic drove it; `dates` for a baseline that kept none. */
  logic: 'links' | 'dates';
}

/** The planned working duration, from the stored figure or from the two planned dates. */
export function workingDuration(a: BaselineActivity, cal: WorkCalendar): number {
  if (typeof a.durationDays === 'number' && Number.isFinite(a.durationDays)) return Math.max(0, a.durationDays);
  return Math.max(1, workingDaysBetween(cal, a.plannedStart, addDays(a.plannedFinish, 1)));
}

/**
 * Re-solve the baseline's logic from what has happened by `statusDate`.
 * `rows` is `varianceFor` on the same baseline, as at the same day.
 */
export function forecastFor(
  baseline: Baseline,
  rows: readonly VarianceRow[],
  statusDate: string,
  calendar: WorkCalendar = baseline.calendar ?? EVERY_DAY,
): Forecast {
  const index = new CalendarIndex(calendar, baseline.projectStart);
  const leaves = baseline.activities.filter((a) => !a.isGroup);
  const rowOf = new Map(rows.map((r) => [r.activityId, r]));
  const links = baseline.links ?? [];
  const logic: Forecast['logic'] = links.length > 0 ? 'links' : 'dates';
  const d = index.indexOf(statusDate);

  const stateOf = new Map<string, ForecastRow['state']>();
  const nodes: CpmNode[] = leaves.map((a) => {
    const r = rowOf.get(a.activityId);
    const duration = workingDuration(a, calendar);
    const base: CpmNode = { id: a.activityId, duration, notBefore: null };

    if (r && r.state === 'complete' && r.completedOn !== null && r.completedOn <= statusDate) {
      stateOf.set(a.activityId, 'complete');
      const start = r.firstEntry !== null && r.firstEntry <= r.completedOn ? r.firstEntry : r.completedOn;
      return { ...base, fixed: { es: index.indexOf(start), ef: index.indexOf(r.completedOn) + 1 } };
    }
    if (r && r.firstEntry !== null && r.firstEntry <= statusDate && r.state !== 'unmatched') {
      stateOf.set(a.activityId, 'in-progress');
      const es = index.indexOf(r.firstEntry);
      const remaining = Math.max(1, duration - Math.max(0, d - es));
      return { ...base, fixed: { es, ef: Math.max(d, es) + remaining } };
    }
    stateOf.set(a.activityId, 'not-started');
    const pinned = a.notBefore ? index.indexOf(a.notBefore) : null;
    const planned = logic === 'dates' ? index.indexOf(a.plannedStart) : null;
    return { ...base, floor: d, notBefore: Math.max(pinned ?? 0, planned ?? 0) || null };
  });

  const cpmLinks: CpmLink[] = links.map((l) => ({ from: l.from, to: l.to, type: l.type, lag: l.lagDays }));
  const { times } = solveCpm(nodes, cpmLinks);

  const out: ForecastRow[] = leaves.map((a) => {
    const t = times.get(a.activityId)!;
    const state = stateOf.get(a.activityId)!;
    const r = rowOf.get(a.activityId);
    const forecastStart =
      state === 'not-started' ? index.dateAt(t.es) : (r?.firstEntry ?? r?.completedOn ?? index.dateAt(t.es));
    const forecastFinish =
      state === 'complete' ? r!.completedOn! : index.dateAt(Math.max(t.es, t.ef - 1));
    return {
      activityId: a.activityId,
      name: a.name,
      kind: a.kind,
      plannedStart: a.plannedStart,
      plannedFinish: a.plannedFinish,
      forecastStart,
      forecastFinish: forecastFinish < forecastStart ? forecastStart : forecastFinish,
      floatDays: state === 'complete' ? null : t.tf,
      critical: state !== 'complete' && t.tf <= 0,
      state,
      slipDays: signedDays(a.plannedFinish, forecastFinish),
    };
  });

  const latest = (xs: string[]): string => xs.reduce((m, x) => (x > m ? x : m), xs[0] ?? baseline.projectStart);
  const baselineFinish = latest(leaves.map((a) => a.plannedFinish));
  const forecastFinish = latest(out.map((r) => r.forecastFinish));

  return {
    statusDate,
    rows: out,
    baselineFinish,
    forecastFinish,
    slipDays: signedDays(baselineFinish, forecastFinish),
    slipWorkingDays: index.indexOf(forecastFinish) - index.indexOf(baselineFinish),
    criticalPath: out
      .filter((r) => r.critical)
      .sort((a, b) => a.forecastStart.localeCompare(b.forecastStart) || a.activityId.localeCompare(b.activityId)),
    logic,
  };
}

/** Days as a phrase: "9 d late", "on time", "3 d early". */
export function slipWord(days: number): string {
  if (days === 0) return 'on time';
  return days > 0 ? `${days} d late` : `${-days} d early`;
}

/**
 * The look-ahead of a period report (LC-55): what the forecast has under way
 * as the period ends, and what it starts in the days that follow. The
 * Monitoring panel's *Planned now* and *Next*, read from the re-solved dates
 * rather than the planned ones, so a late activity is looked ahead to when it
 * will actually run.
 */
export function lookAhead(
  f: Forecast,
  days: number,
): { from: string; to: string; underway: ForecastRow[]; starting: ForecastRow[] } {
  const from = addDays(f.statusDate, 1);
  const to = addDays(f.statusDate, Math.max(1, days));
  const open = f.rows.filter((r) => r.state !== 'complete');
  const byStart = (a: ForecastRow, b: ForecastRow) =>
    a.forecastStart.localeCompare(b.forecastStart) || a.activityId.localeCompare(b.activityId);
  return {
    from,
    to,
    underway: open.filter((r) => r.forecastStart <= f.statusDate && r.forecastFinish >= f.statusDate).sort(byStart),
    starting: open.filter((r) => r.forecastStart > f.statusDate && r.forecastStart <= to).sort(byStart),
  };
}
